import { type DropdownProps } from "semantic-ui-react";

import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useMemo } from "react";
import { ContentTypesEnum } from "../../../enums";
import MultiStateDropdown, { type MultiStateDropdownOption } from "../multiStateDropdown/MultiStateDropdown";
import { FaIcons, Icons, RenderIcons } from "./constants";

const getOption = (label: ContentTypesEnum) => ({
  label,
  iconName: Icons[label],
  icon: FaIcons[label],
  renderIcon: RenderIcons[label],
});

const createFlowOption = (contentSchedulingEnabled: boolean): MultiStateDropdownOption<ContentTypesEnum> => {
  if (!contentSchedulingEnabled) {
    return getOption(ContentTypesEnum.Flows);
  }

  return {
    label: ContentTypesEnum.Flows,
    iconName: Icons.Flows,
    options: [getOption(ContentTypesEnum.Scheduled), getOption(ContentTypesEnum.AllFlows)],
  };
};

const getContentOptions = (
  embeddedVideosEnabled: boolean,
  linkAssetTypeEnabled: boolean,
  contentSchedulingEnabled: boolean,
): MultiStateDropdownOption<ContentTypesEnum>[] => {
  const videoOptions: MultiStateDropdownOption<ContentTypesEnum>[] = [getOption(ContentTypesEnum.Uploaded)];

  if (embeddedVideosEnabled) {
    videoOptions.push(getOption(ContentTypesEnum.Embedded));
  }

  const assetsOptions: MultiStateDropdownOption<ContentTypesEnum>[] = [
    {
      label: ContentTypesEnum.Videos,
      iconName: Icons.Videos,
      options: videoOptions,
    },
    getOption(ContentTypesEnum.Surveys),
    getOption(ContentTypesEnum.Assessments),
    getOption(ContentTypesEnum.PDFs),
  ];

  if (linkAssetTypeEnabled) {
    assetsOptions.push(getOption(ContentTypesEnum.Links));
  }

  return [
    createFlowOption(contentSchedulingEnabled),
    {
      label: ContentTypesEnum.Assets,
      iconName: Icons.Assets,
      options: assetsOptions,
    },
    getOption(ContentTypesEnum.Events),
  ];
};

const addOptions: MultiStateDropdownOption<ContentTypesEnum>[] = [getOption(ContentTypesEnum.Flows)];

interface ContentSegmentationDropdownProps extends DropdownProps {
  options?: MultiStateDropdownOption<ContentTypesEnum>[];
  defaultValue?: ContentTypesEnum;
  showScheduledContent?: boolean;
}

// temporal
export function AddContentSegmentationDropdown(props: ContentSegmentationDropdownProps) {
  return (
    <MultiStateDropdown
      className="content-segmentation-dropdown"
      options={addOptions}
      defaultOption={props.defaultValue ? getOption(props.defaultValue) : addOptions[0]}
      {...props}
    />
  );
}

export default function ContentSegmentationDropdown(props: ContentSegmentationDropdownProps) {
  const embeddedVideoEnabled = useFeatureFlag(FeatureFlags.EmbeddedVideos);
  const linkAssetTypeEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  const contentSchedulingFlag = useFeatureFlag(FeatureFlags.ContentScheduling);
  const contentSchedulingEnabled = !!props.showScheduledContent && !!contentSchedulingFlag;
  const contentOptions = useMemo(
    () => getContentOptions(embeddedVideoEnabled, linkAssetTypeEnabled, contentSchedulingEnabled),
    [embeddedVideoEnabled, linkAssetTypeEnabled, contentSchedulingEnabled],
  );

  const defaultOption = useMemo(() => {
    const value = props.defaultValue ?? (contentSchedulingEnabled ? ContentTypesEnum.AllFlows : ContentTypesEnum.Flows);
    return getOption(value);
  }, [contentSchedulingEnabled, props.defaultValue]);

  return (
    <MultiStateDropdown
      className="content-segmentation-dropdown"
      options={contentOptions}
      defaultOption={defaultOption}
      {...props}
    />
  );
}
